import React, { createContext, useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowDown, ArrowRight, Loader2 } from "lucide-react";

/**
 * One destination on this page: the application itself.
 *
 *   /business-funding/<introducer-slug>  → their form, their attribution
 *   /business-funding                    → the desk's own door (/apply/connexis-direct)
 *
 * A returning visitor picks up wherever they left off. The callback form lives in exactly one
 * place — the FAQ panel — so nothing else on the page competes with applying.
 */
const DeskCtx = createContext({ openDesk: () => {}, applyHref: null, introducer: null, mode: null });
export const useFundingCta = () => useContext(DeskCtx);

function viewerId() {
  try {
    let v = localStorage.getItem("cx_viewer");
    if (!v) {
      v = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("cx_viewer", v);
    }
    return v;
  } catch { return null; }
}

export function FundingCtaProvider({ children }) {
  const { slug } = useParams();
  const [open, setOpen] = useState(false);
  const [applyHref, setApplyHref] = useState(null);
  const [introducer, setIntroducer] = useState(null);
  const [mode, setMode] = useState(null);

  useEffect(() => {
    const directDoor = () => api.get("/apply/direct")
      .then((r) => { setApplyHref(r.data?.apply_url || null); setMode("direct"); })
      .catch(() => setMode("direct"));

    if (slug) {
      const viewer = viewerId();
      api.get(`/funding/link/${slug}`, {
        params: { ...(viewer ? { viewer } : {}), page: window.location.pathname.split("/")[1] || "business-funding" },
      })
        .then((r) => {
          setIntroducer(r.data?.introducer || null);
          setApplyHref(r.data?.apply_url || `/apply/${slug}`);
          setMode("intro");
          try { localStorage.setItem("cx_apply_slug", slug); } catch { /* private browsing */ }
        })
        .catch(directDoor);      // unknown or switched-off link — still let them apply
      return;
    }
    let saved = null;
    try { saved = localStorage.getItem("cx_apply_slug"); } catch { /* private browsing */ }
    if (saved) {
      setApplyHref(`/apply/${saved}`);
      setMode("resume");
      return;
    }
    directDoor();
  }, [slug]);

  return (
    <DeskCtx.Provider value={{ openDesk: () => setOpen(true), applyHref, introducer, mode }}>
      {children}
      <TalkToDeskDialog open={open} onOpenChange={setOpen} />
    </DeskCtx.Provider>
  );
}

/**
 * Into the application. `params` carries anything already known — the eligibility answers, for
 * instance — so the form opens part-completed rather than blank.
 */
export function PrimaryCta({ label, params, className = "", testId = "funding-primary-cta" }) {
  const { applyHref, openDesk, mode } = useFundingCta();
  const classes = `group inline-flex items-center justify-center gap-2.5 h-14 px-8 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white text-[15px] font-semibold transition-colors ${className}`;

  if (applyHref) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : "";
    return (
      <Link to={`${applyHref}${qs}`} className={classes} data-testid={testId}>
        {label || (mode === "resume" ? "Continue your application" : "Start your application")}
        <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    );
  }
  return (
    <button onClick={openDesk} className={classes} data-testid={testId}>
      {label || "Speak to the funding desk"}
      <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
    </button>
  );
}

/** Navigation, never a second form — it moves you to the part of the page you asked about. */
export function SecondaryCta({ label = "How it works", target = "funding-routing",
                               className = "", testId = "funding-secondary-cta" }) {
  const jump = () => document.querySelector(`[data-testid="${target}"]`)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <button onClick={jump} data-testid={testId}
            className={`inline-flex items-center justify-center gap-2.5 h-14 px-7 rounded-full border border-white/15 hover:border-white/35 hover:bg-white/[0.04] text-white text-[15px] font-medium transition-colors ${className}`}>
      <ArrowDown className="size-4" /> {label}
    </button>
  );
}

/** The page's only callback form. Kept short on purpose: five lines, then we call them. */
function TalkToDeskDialog({ open, onOpenChange }) {
  const [f, setF] = useState({ name: "", business: "", email: "", phone: "", amount: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const ready = f.name.trim() && /\S+@\S+\.\S+/.test(f.email) && f.phone.replace(/\D/g, "").length >= 7;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    try {
      await api.post("/contact", {
        name: f.name.trim(),
        email: f.email.trim(),
        subject: `Funding enquiry${f.amount ? ` — ${f.amount}` : ""}`,
        message: [
          `Business: ${f.business.trim() || "not given"}`,
          `Phone: ${f.phone.trim()}`,
          `Facility size: ${f.amount.trim() || "not stated"}`,
          "",
          "— sent from the business funding page",
        ].join("\n"),
      });
      toast.success("Received. Someone from the desk will call you back today.");
      onOpenChange(false);
      setF({ name: "", business: "", email: "", phone: "", amount: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "That didn't send — try again or email ops@connex.is");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="funding-desk-dialog"
        className="max-w-md w-[calc(100%-1.5rem)] max-h-[86dvh] overflow-y-auto p-5 sm:p-6 gap-4
                   [&>button:last-child]:size-10 [&>button:last-child]:grid [&>button:last-child]:place-items-center
                   [&>button:last-child]:rounded-full [&>button:last-child]:bg-slate-100 [&>button:last-child]:opacity-100
                   [&>button:last-child>svg]:size-5">
        <DialogHeader className="pr-12 space-y-1.5 text-left">
          <DialogTitle className="text-[19px] tracking-tight">Speak to the desk</DialogTitle>
          <DialogDescription className="text-[13px] leading-snug">
            Five lines and we'll call you back today. No credit search, no obligation.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name"><Input value={f.name} onChange={set("name")} placeholder="Sam Whitaker" className="h-11" data-testid="desk-name" /></Field>
            <Field label="Business"><Input value={f.business} onChange={set("business")} placeholder="Whitaker Industrial Ltd" className="h-11" data-testid="desk-business" /></Field>
            <Field label="Email"><Input type="email" value={f.email} onChange={set("email")} placeholder="sam@whitaker.co.uk" className="h-11" data-testid="desk-email" /></Field>
            <Field label="Best number"><Input type="tel" value={f.phone} onChange={set("phone")} placeholder="07123 456789" className="h-11" data-testid="desk-phone" /></Field>
          </div>
          <Field label="Facility size" hint="A range is fine">
            <Input value={f.amount} onChange={set("amount")} placeholder="£750,000" className="h-11" data-testid="desk-amount" />
          </Field>
          <Button type="submit" disabled={!ready || busy} className="w-full h-12 rounded-full" data-testid="desk-submit">
            {busy ? <Loader2 className="size-4 animate-spin" /> : "Request a call back"}
          </Button>
          <p className="text-[11.5px] text-muted-foreground leading-relaxed">
            These details are used to talk to you about funding and nothing else. They are never sold on.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[12.5px] font-medium text-foreground">
        {label}{hint && <span className="ml-1.5 font-normal text-muted-foreground">· {hint}</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
